import { createApi } from '@reduxjs/toolkit/query/react';
import axios, { AxiosProgressEvent } from 'axios';
import { baseQuerySample, headerIdConfigs } from '../helpers';
import { BASE_URL, X_HEADER_ID_NAME } from '@src/utils/constants';
import { hasParams } from '@src/utils/helpers';
import { sessionSetter } from '@utils/storage';

type ContractorFilterBody = {
  contractorIds: number[];
  startDate: string;
  endDate: string;
};

export type ContractorMonthlyFilterBody = {
  contractorId: number;
  networkAverage: boolean;
  networkAverageContractorIds: number[];
  startDate?: string;
  endDate?: string;
};

type ContractorFilterArgs = {
  contractorId?: number;
  body: ContractorFilterBody;
};

type ContractorPagedFilterArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorFilterBody;
};

type ContractorSegmentIdsPagedArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  segmentIds: number[];
};

type ContractorMonthlyPagedFilterArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorMonthlyAggregateFilterBody;
};

export type ContractorMonthlyAggregateFilterBody = {
  contractorIds: number[];
  startDate?: string;
  endDate?: string;
  date?: string;
  range?: string;
  networkAverage?: boolean;
  networkAverageContractorIds?: number[];
};

type ContractorSegmentsResponse<T = any> = {
  content: T[];
  totalElements: number;
  tableLabelName?: string;
};

type ContractorRideTypeChartBody = {
  totalSegmentCount: number;
  scheduledCount: number;
  willCallCount: number;
  scheduledPercentage: number;
  willCallPercentage: number;
};

type ContractorItem = {
  label: string;
  costPerRide: number;
  totalRideCount: number;
};

type ContractorCostPerRideTrendAggregateBody = ContractorFilterBody & {
  date: string;
  range: string;
};

type ContractorCostPerRideTrendAggregateArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorCostPerRideTrendAggregateBody;
};

type ContractorCostPerRideTrendAggregateItem = {
  contractorId: number;
  contractorName: string;
  contractorStatus: string;
  firstDateOfService: string;
  lastDateOfService: string;
  atmSoftware: string;
  countOfRides: number;
  totalRideCost: number;
  costPerRide: number;
};

type ContractorRideCompletionRateAggregateArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorCostPerRideTrendAggregateBody;
};

type ContractorRideCompletionRateAggregateItem = {
  contractorId: number;
  contractorName: string;
  contractorStatus: string;
  firstDateOfService: string;
  lastDateOfService: string;
  atmSoftware: string;
  totalRideCount: number;
  totalCompletedRideCount: number;
  rideCompletionRate: number;
};

type ContractorRideTypeComparisonAggregateArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorCostPerRideTrendAggregateBody;
};

type ContractorRideTypeComparisonAggregateItem = {
  contractorId: number;
  contractorName: string;
  contractorStatus: string;
  firstDateOfService: string;
  lastDateOfService: string;
  countOfScheduledRides: number;
  percentageScheduleType: number;
  countWillCallRides: number;
  percentageWillCallType: number;
};

type ContractorArrivalAverageTimeAggregateArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorCostPerRideTrendAggregateBody;
};

type ContractorArrivalAverageTimeAggregateItem = {
  contractorId: number;
  contractorName: string;
  contractorStatus: string;
  firstDateOfService: string;
  lastDateOfService: string;
  averageArrivalTime: number;
  totalAssignedRides: number;
};

type Top5IncompleteDriverNoShowCitiesAggregateArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorCostPerRideTrendAggregateBody;
};

type Top5IncompleteDriverNoShowCitiesAggregateItem = {
  contractorId: number;
  contractorName: string;
  contractorStatus: string;
  firstDateOfService: string;
  lastDateOfService: string;
  pickUpAddressCity: string;
  noShowCount: number;
};

type ContractorRidesByLosAggregateArgs = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  body: ContractorCostPerRideTrendAggregateBody;
};

type ContractorRidesByLosAggregateItem = {
  contractorId: number;
  contractorName: string;
  contractorStatus: string;
  firstDateOfService: string;
  lastDateOfService: string;
  los: string;
  totalRideCount: number;
};

type ContractorNoShowItem = {
  label: string;
  completionRate: number;
  totalRideCount: number;
};

export type ContractorCostComparisonResponse = {
  totalSegmentCount: number;
  completedCostTotal: number;
  noShowCostTotal: number;
  completedCostPercentage: number;
  noShowCostPercentage: number;
};

export type ContractorRideTypeComparisonResponse = {
  totalSegmentCount: number;
  scheduledCount: number;
  willCallCount: number;
  scheduledPercentage: number;
  willCallPercentage: number;
};

export type ContractorCityNoShowResponse = {
  locationId: number;
  city: string;
  noShowCount: number;
};

export type ContractorLevelOfServiceResponse = {
  losId: number;
  levelOfServiceName: string;
  segmentCount: number;
};

export type ContractorArrivalAverageTimeResponse = {
  label: string;
  weekEnd: string;
  averageArrivalTimeMinutes: number;
  totalQualifyingRides: number;
};

export type ContractorMonthlyCostResponse = {
  contractorId: number;
  contractorName: string;
  month: string;
  totalCost: number;
};

export type ContractorMonthlyCountResponse = {
  contractorId: number;
  contractorName: string;
  month: string;
  segmentCount: number;
};

export type ContractorMonthlyOtpResponse = {
  contractorId: number;
  contractorName: string;
  month: string;
  onTimeCount: number;
  lateCount: number;
  totalCount: number;
  onTimePercentage: number;
  isNetworkAverage?: boolean;
};

export type ContractorMonthlyLosCostResponse = {
  month: string;
  losId: number;
  losName: string;
  totalCost: number;
};

export type ContractorMonthlyLosCountResponse = {
  month: string;
  losId: number;
  levelOfServiceName: string;
  segmentCount: number;
};

export type ContractorMonthlyCountyCostResponse = {
  month: string;
  county: string;
  totalCost: number;
};

export type ContractorMonthlyCountyCountResponse = {
  countyId: number;
  countyName: string;
  month: string;
  segmentCount: number;
};

export type ContractorMonthlyRidesByStatusResponse = {
  contractorId: number;
  contractorName: string;
  month: string;
  verifiedCompleteCount: number;
  memberNoShowCount: number;
  driverNoShowCount: number;
  canceledCount: number;
};

type ContractorMonthlyCostReportResponse = {
  monthlyCostPerContractor?: ContractorMonthlyCostResponse[];
  monthlyCostNetworkAverage?: Array<{
    month: string;
    totalCost: number;
  }>;
};

type ContractorMonthlyCountReportResponse = {
  monthlyCountPerContractor?: ContractorMonthlyCountResponse[];
  monthlyCountNetworkAverage?: Array<{
    month: string;
    networkAverage: number;
  }>;
};

type ContractorMonthlyOtpReportResponse = {
  monthlyOtpPerContractor?: ContractorMonthlyOtpResponse[];
  monthlyOtpNetworkAverage?: MonthlyNetworkAverageResponse[];
};

type ContractorMonthlyLosCostReportResponse = {
  monthlyCostPerLos?: ContractorMonthlyLosCostResponse[];
  monthlyCostNetworkAveragePerLos?: Array<{
    month: string;
    losId: number;
    losName: string;
    networkAverage: number;
  }>;
};

type ContractorMonthlyLosCountReportResponse = {
  monthlyCountPerLos?: ContractorMonthlyLosCountResponse[];
  monthlyCountNetworkAveragePerLos?: Array<{
    month: string;
    losId: number;
    networkAverage: number;
  }>;
};

type ContractorMonthlyCountyCostReportResponse = {
  monthlyCostPerCounty?: ContractorMonthlyCountyCostResponse[];
  monthlyCostNetworkAveragePerCounty?: Array<{
    month: string;
    county: string;
    networkAverage: number;
  }>;
};

type ContractorMonthlyCountyCountReportResponse = {
  monthlyCountPerCounty?: ContractorMonthlyCountyCountResponse[];
  monthlyCountNetworkAveragePerCounty?: Array<{
    month: string;
    countyId: number;
    countyName: string;
    networkAverage: number;
  }>;
};

type MonthlyNetworkAverageResponse = {
  month: string;
  networkAverage: number;
};

const NETWORK_AVERAGE_CONTRACTOR_ID = -1;
const NETWORK_AVERAGE_LABEL = 'Network Average';

type ContractorLosChartBody = {
  losId: number;
  levelOfServiceName: string;
  segmentCount: number;
};

type CityNoShowSegmentsItem = {
  content: [
    segmentId: number,
    rideDate: string,
    scheduledPickupTime: {
      hour: number;
      minute: number;
      second: number;
      nano: number;
    },
    confirmationCode: string,
  ];
};
type ContractorCostComparisonChartItem = {
  contractorId: number;
  body: ContractorFilterBody;
};

type ContractorCityNoShowRankingItem = {
  contractorId: number;
  body: ContractorFilterBody;
};

type ContractorRideTypeChartItem = {
  contractorId: number;
  body: ContractorRideTypeChartBody;
};

type ContractorLosChartItem = {
  contractorId: number;
  body: ContractorLosChartBody;
};

type ContractorCityNoShowSegmentsItem = {
  contractorId: number;
  body: CityNoShowSegmentsItem;
};

type ContractorCityNoShowAggregateItem = {
  contractorId: number;
  body: {
    contractorId: number;
    contractorName: string;
    locationId: number;
    pickupCity: string;
    noShowCount: number;
    startDate: string;
    endDate: string;
  };
};

type DashboardRideExportArgs = {
  params: Record<string, any>;
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
};
type AvgRideCostPerMemberMonthlyStat = {
  month: string;
  avgRidesPerMember?: number;
  avrRidesPerMember?: number;
  avRidesPerMember?: number;
  avgCostPerMember?: number;
};

type AvgRideCostPerMemberResponse = {
  avgRidesPerMember?: number;
  avrRidesPerMember?: number;
  avRidesPerMember?: number;
  avgCostPerMember?: number;
  avgCostPerRide?: number;
  monthlyRideStats?: AvgRideCostPerMemberMonthlyStat[];
};

type MemberMonthActiveResponseItem = {
  month: string;
  activeMembers?: number;
  inactiveMembers?: number;
};

export const CONTRACTOR_TABLE_URLS = {
  topCostSegments: '/v2/search/contractor/top-cost/segments',
  topCostMembers: '/v2/search/contractor/top-cost/members',
  noShowSegments: '/v2/search/contractor/no-show/segments',
  dashboardSegments: '/v2/search/contractor/dashboard/segments',
  dashboardSegmentsExport:
    '/v2/search/contractor/dashboard/drill-through/segments/export',
  costComparisonAggregate:
    '/v2/search/contractor/dashboard/cost-comparison/aggregate',
  reportSegments: '/v2/search/contractor/report/segments',
  reportSegmentsExport:
    '/v2/search/contractor/report/drill-through/segments/export',
  costPerRideTrendAggregate:
    '/v2/search/contractor/dashboard/cost/per-ride-trend/aggregate',
  rideCompletionRateAggregate:
    '/v2/search/contractor/dashboard/ride/completion-rate/aggregate',
  rideTypeComparisonAggregate:
    '/v2/search/contractor/dashboard/ride-type-comparison/aggregate',
  arrivalAverageTimeAggregate:
    '/v2/search/contractor/dashboard/arrival-average-time/aggregate',
  cityNoShowAggregate: '/v2/search/contractor/dashboard/city-no-show/aggregate',
  ridesByLosAggregate: '/v2/search/contractor/dashboard/rides/los/aggregate',
  monthlyCostPerCompanyAggregate:
    '/v2/search/contractor/report/monthly-cost/company/aggregate',
  monthlyCountPerCompanyAggregate:
    '/v2/search/contractor/report/monthly-count/company/aggregate',
  monthlyOnTimePerformanceAggregate:
    '/v2/search/contractor/report/monthly/on-time-performance/aggregate',
  monthlyCostPerLosAggregate:
    '/v2/search/contractor/report/monthly-cost/los/aggregate',
  monthlyCountPerLosAggregate:
    '/v2/search/contractor/report/monthly-count/los/aggregate',
  monthlyCostPerCountyAggregate:
    '/v2/search/contractor/report/monthly-cost/county/aggregate',
  monthlyCountPerCountyAggregate:
    '/v2/search/contractor/report/monthly-count/county/aggregate',
  monthlyRidesByStatusAggregate:
    '/v2/search/contractor/report/monthly/rides-by-status/aggregate',
} as const;

export const contractorTableExportUrl = (url: string) => `${url}/export`;

export const dashboardChartsApi = createApi({
  reducerPath: 'RideShareDashboard',
  baseQuery: baseQuerySample,

  endpoints: (builder) => ({
    getRideStatuses: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/status`,
        params,
      }),
    }),

    getStatisticBox: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/combine`,
        params,
      }),
    }),
    // TODO remove this after check
    // getRideCompleteBox: builder.query({
    //   query: (params) => ({
    //     url: `/v1/search/dashboard/ride/complete`,
    //     params,
    //   }),
    // }),
    getRideCostByCounty: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/cost-by-county`,
        params,
      }),
    }),
    // TODO remove this after check
    // getRideDistinctMembers: builder.query({
    //   query: (params) => ({
    //     url: `/v1/search/dashboard/ride/distinct/members`,
    //     params,
    //   }),
    // }),
    // TODO remove this after check
    // getRideOnTimePerformance: builder.query({
    //   query: (params) => ({
    //     url: `/v1/search/dashboard/ride/on-time-performance/percentage`,
    //     params,
    //   }),
    // }),
    // TODO remove this after check
    // getRideCost: builder.query({
    //   query: (params) => ({
    //     url: `/v1/search/dashboard/ride/cost`,
    //     params,
    //   }),
    // }),
    getRideInCompleteBreakDown: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/incomplete/breakdown`,
        params,
      }),
    }),
    getRideLsVsRoutine: builder.query({
      query: (queryParams = {}) => ({
        url: `/v1/search/dashboard/ride/ls-vs-routine`,
        params: hasParams(queryParams)
          ? { range: 'custom', ...queryParams }
          : undefined,
      }),
    }),
    getRideInCompleteLS: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/incomplete-ls/breakdown`,
        params,
      }),
    }),
    getRideSearchLocation: builder.query({
      query: (queryParams = {}) => ({
        url: `/v1/search/dashboard/ride/top/location`,
        params: hasParams(queryParams)
          ? { range: 'custom', ...queryParams }
          : undefined,
      }),
    }),
    getRideMonthStatus: builder.query({
      query: (params?: any) => ({
        url: `/v1/search/dashboard/ride/month/status`,
        params: params,
      }),
    }),
    getRideMonthAllStatus: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/month/all-status`,
        params,
      }),
    }),
    getRideMonthsPerformance: builder.query({
      query: (queryParams = {}) => ({
        url: `/v1/search/dashboard/ride/on-time-performance/month/percentage`,
        params: hasParams(queryParams)
          ? { range: 'custom', ...queryParams }
          : undefined,
      }),
    }),
    getInterValOnTimePerformance: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/interval/on-time-performance`,
        params,
      }),
    }),
    getInterValOnLSTimePerformance: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/ls-interval/on-time-performance`,
        params,
      }),
    }),

    getRideTopServiceTypes: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/top/service-type?`,
        params,
      }),
    }),
    getRideTopLocation: builder.query({
      query: () => ({
        url: `/v1/search/dashboard/ride/top/location`,
      }),
    }),
    getRideDashboardMember: builder.query({
      query: (queryParams = {}) => ({
        url: `/v1/search/dashboard/ride/member`,
        params: hasParams(queryParams)
          ? { range: 'custom', ...queryParams }
          : undefined,
      }),
    }),
    getRideDashboardHeatMap: builder.query({
      query: (queryParams = {}) => {
        return {
          url: 'v1/search/dashboard/ride/heat-map',
          params: hasParams(queryParams)
            ? { range: 'custom', ...queryParams }
            : undefined,
        };
      },
    }),

    getRideDashboardAverageMiles: builder.query({
      query: (queryParams = {}) => ({
        url: `/v1/search/dashboard/ride/average-miles`,
        params: hasParams(queryParams)
          ? { range: 'custom', ...queryParams }
          : undefined,
      }),
    }),

    getMemberDashboardMonthShowCount: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/month/consecutive/no-show/detail`,
        params,
      }),
    }),

    getDashboardRecentRide: builder.query({
      query: () => ({
        url: `/v1/search/dashboard/ride/recent-rides`,
      }),
    }),
    // REPORTS MEMBER DASHBOARD
    getTopNoShowCount: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/top/no-show/count`,
        params,
      }),
    }),
    getMemberTOPLobData: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/top/lob`,
        params,
      }),
    }),
    getTopHighMileage: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/top/high-mileage`,
        params,
      }),
    }),

    getActiveMembers: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/active`,
        params,
      }),
    }),

    getTopUsedTags: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/top/used-tags`,
        params,
      }),
    }),

    getTopRenderingService: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/top/rendering-service`,
        params,
      }),
    }),

    getTopCounty: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/top/county`,
        params,
      }),
    }),

    getMonthCompleteRide: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/month/complete-ride`,
        params,
      }),
    }),

    getMonthAvgRideCostPerMember: builder.query<
      AvgRideCostPerMemberResponse,
      any
    >({
      query: (params) => ({
        url: `/v1/search/dashboard/member/month/avg-ride-per-member-and-cost`,
        params,
      }),
    }),

    getMonthActiveMembers: builder.query<MemberMonthActiveResponseItem[], any>({
      query: (params) => ({
        url: `/v1/search/dashboard/member/month/active`,
        params,
      }),
    }),

    getMonthCreatedDate: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/month/created-date`,
        params,
      }),
    }),
    getMonthAgeGroups: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/member/month/age-groups`,
        params,
      }),
    }),
    // REPORTS RIDESHARE DASHBOARD
    getRideReportsRideShare: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/rideshare/top/incomplete/member-rides`,
        params,
      }),
    }),
    // TopCostSegments
    getContractorTopCostSegments: builder.mutation<
      ContractorSegmentsResponse,
      ContractorPagedFilterArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorPagedFilterArgs) => ({
        url: CONTRACTOR_TABLE_URLS.topCostSegments,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorTopCostRanking: builder.mutation<
      ContractorItem[],
      ContractorFilterBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/top-cost/ranking`,
        method: 'POST',
        body,
      }),
    }),
    getContractorTopCostMembers: builder.mutation<
      ContractorItem[],
      ContractorPagedFilterArgs
    >({
      query: ({ page = 0, size = 10, body }: ContractorPagedFilterArgs) => ({
        url: CONTRACTOR_TABLE_URLS.topCostMembers,
        method: 'POST',
        params: {
          page,
          size,
        },
        body,
      }),
    }),
    // Cost Per Ride Trend - contractor aggregate (top-5-contractor-cost "See Data" table)
    getCostPerRideTrendAggregate: builder.mutation<
      ContractorSegmentsResponse<ContractorCostPerRideTrendAggregateItem>,
      ContractorCostPerRideTrendAggregateArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorCostPerRideTrendAggregateArgs) => ({
        url: CONTRACTOR_TABLE_URLS.costPerRideTrendAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    // Contractor Ride Completion Rate - contractor aggregate (top-contractor-incomplete-driver-no-show-ride-count "See Data" table)
    getContractorRideCompletionRateAggregate: builder.mutation<
      ContractorSegmentsResponse<ContractorRideCompletionRateAggregateItem>,
      ContractorRideCompletionRateAggregateArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorRideCompletionRateAggregateArgs) => ({
        url: CONTRACTOR_TABLE_URLS.rideCompletionRateAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    // Ride Type Comparison - contractor aggregate (ride-type-comparison "See Data" table)
    getContractorRideTypeComparisonAggregate: builder.mutation<
      ContractorSegmentsResponse<ContractorRideTypeComparisonAggregateItem>,
      ContractorRideTypeComparisonAggregateArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorRideTypeComparisonAggregateArgs) => ({
        url: CONTRACTOR_TABLE_URLS.rideTypeComparisonAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorAverageTimeToArrivalAggregate: builder.mutation<
      ContractorSegmentsResponse<ContractorArrivalAverageTimeAggregateItem>,
      ContractorArrivalAverageTimeAggregateArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorArrivalAverageTimeAggregateArgs) => ({
        url: CONTRACTOR_TABLE_URLS.arrivalAverageTimeAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    // Top 5 Incomplete - Driver No Show Cities - contractor aggregate (top-5-incomplete-driver-no-show-cities "See Data" table)
    getTop5IncompleteDriverNoShowCitiesAggregate: builder.mutation<
      ContractorSegmentsResponse<Top5IncompleteDriverNoShowCitiesAggregateItem>,
      Top5IncompleteDriverNoShowCitiesAggregateArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: Top5IncompleteDriverNoShowCitiesAggregateArgs) => ({
        url: CONTRACTOR_TABLE_URLS.cityNoShowAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    // Rides by Level of Service - contractor aggregate (rides-by-level-of-service "See Data" table)
    getContractorRidesByLosAggregate: builder.mutation<
      ContractorSegmentsResponse<ContractorRidesByLosAggregateItem>,
      ContractorRidesByLosAggregateArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorRidesByLosAggregateArgs) => ({
        url: CONTRACTOR_TABLE_URLS.ridesByLosAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    // NoShowSegments
    getContractorNoShowSegments: builder.mutation<
      ContractorSegmentsResponse,
      ContractorPagedFilterArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorPagedFilterArgs) => ({
        url: CONTRACTOR_TABLE_URLS.noShowSegments,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),

    getContractorNoShowRanking: builder.mutation<
      ContractorNoShowItem[],
      ContractorFilterBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/no-show/ranking`,
        method: 'POST',
        body,
      }),
    }),
    getContractorNoShowAggregated: builder.mutation({
      query: ({ contractorId, body }: ContractorFilterArgs) => ({
        url: `v2/search/contractor/no-show/aggregate`,
        method: 'POST',
        params: {
          contractorId,
        },
        body,
      }),
    }),
    getContractorCityNoShowRanking: builder.mutation({
      query: ({ contractorId, body }: ContractorCityNoShowRankingItem) => ({
        url: `v2/search/contractor/city-no-show/ranking`,
        method: 'POST',
        params: {
          contractorId,
        },
        body,
      }),
    }),
    getContractorCityNoShowSegments: builder.mutation<
      ContractorSegmentsResponse,
      ContractorPagedFilterArgs
    >({
      query: ({
        page = 0,
        size = 100,
        sortBy,
        sortDir,
        body,
      }: ContractorPagedFilterArgs) => ({
        url: `v2/search/contractor/city-no-show/segments`,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),

    // CityNoShowAggregate
    getContractorCityNoShowAggregate: builder.mutation({
      query: ({ contractorId, body }: ContractorCityNoShowAggregateItem) => ({
        url: `v2/search/contractor/city-no-show/aggregate`,
        method: 'POST',
        params: {
          contractorId,
        },
        body,
      }),
    }),

    // RideTypeChart
    getContractorRideTypeChart: builder.mutation({
      query: ({ contractorId, body }: ContractorRideTypeChartItem) => ({
        url: `v2/search/contractor/ride-type/chart`,
        method: 'POST',
        params: {
          contractorId,
        },
        body,
      }),
    }),
    getContractorLosChart: builder.mutation({
      query: ({ contractorId, body }: ContractorLosChartItem) => ({
        url: `v2/search/contractor/los/chart`,
        method: 'POST',
        params: {
          contractorId,
        },
        body,
      }),
    }),
    // CostComparisonChart
    getContractorCostComparisonChart: builder.mutation({
      query: ({ contractorId, body }: ContractorCostComparisonChartItem) => ({
        url: `v2/search/contractor/cost-comparison/chart`,
        method: 'POST',
        params: {
          contractorId,
        },
        body,
      }),
    }),
    getContractorCostComparisonAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.costComparisonAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorReportSegments: builder.mutation<
      ContractorSegmentsResponse,
      ContractorSegmentIdsPagedArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, segmentIds }) => ({
        url: CONTRACTOR_TABLE_URLS.reportSegments,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body: segmentIds,
      }),
    }),
    getContractorDashboardSegments: builder.mutation<
      ContractorSegmentsResponse,
      ContractorSegmentIdsPagedArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, segmentIds }) => ({
        url: CONTRACTOR_TABLE_URLS.dashboardSegments,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body: segmentIds,
      }),
    }),

    exportRideTableFile: builder.mutation<Blob, DashboardRideExportArgs>({
      queryFn: async ({ params, onDownloadProgress }) => {
        try {
          const config =
            sessionSetter.getItem<Record<string, any>>(X_HEADER_ID_NAME);
          const payorId = config?.payor;
          const headers = payorId
            ? { [headerIdConfigs.payor]: String(payorId) }
            : undefined;

          const response = await axios({
            url: `${BASE_URL}/v2/search/dashboard/ride/table/export`,
            method: 'POST',
            responseType: 'blob',
            withCredentials: true,
            data: { ...params },
            headers,
            onDownloadProgress,
          });

          return { data: response.data };
        } catch (error: any) {
          return {
            error: {
              status: error?.response?.status ?? 'FETCH_ERROR',
              data: error?.response?.data ?? error?.message ?? 'Export failed',
            },
          };
        }
      },
    }),
    // Cost Per Ride Trend
    getCostPerRideTrend: builder.query<
      ContractorItem[],
      ContractorCostPerRideTrendAggregateBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/dashboard/cost/per-ride-trend`,
        method: 'POST',
        body,
      }),
    }),
    // Contractor Ride Completion Rate
    getContractorRideCompletionRate: builder.query<
      ContractorNoShowItem[],
      ContractorCostPerRideTrendAggregateBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/dashboard/ride/completion-rate`,
        method: 'POST',
        body,
      }),
    }),
    // Contractor Cost Comparison
    getContractorCostComparison: builder.query<
      ContractorCostComparisonResponse,
      ContractorFilterBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/dashboard/cost-comparison`,
        method: 'POST',
        body,
      }),
    }),
    // Contractor Average Time to Arrival
    getContractorAverageTimeToArrival: builder.query<
      ContractorArrivalAverageTimeResponse[],
      ContractorCostPerRideTrendAggregateBody
    >({
      query: (body: ContractorCostPerRideTrendAggregateBody) => ({
        url: `/v2/search/contractor/dashboard/arrival-average-time`,
        method: 'POST',
        body,
      }),
    }),
    // Ride Type Comparison
    getRideTypeComparison: builder.query<
      ContractorRideTypeComparisonResponse,
      ContractorFilterBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/dashboard/ride-type/comparison`,
        method: 'POST',
        body,
      }),
    }),
    // Top 5 Incomplete - Driver No Show Cities
    getTop5IncompleteDriverNoShowCities: builder.query<
      ContractorCityNoShowResponse[],
      ContractorFilterBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/dashboard/city-no-show/ranking`,
        method: 'POST',
        body,
      }),
    }),
    // Rides by Level of Service
    getRidesByLevelOfService: builder.query<
      ContractorLevelOfServiceResponse[],
      ContractorFilterBody
    >({
      query: (body: ContractorFilterBody) => ({
        url: `/v2/search/contractor/dashboard/rides/los`,
        method: 'POST',
        body,
      }),
    }),
    // Contractor Cost Per Company
    getContractorCostPerCompany: builder.query<
      ContractorMonthlyCostResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly-cost/company`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyCostResponse[]
          | ContractorMonthlyCostReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) =>
        Array.isArray(response)
          ? response
          : [
              ...(response.monthlyCostPerContractor ?? []),
              ...(arg.networkAverage
                ? (response.monthlyCostNetworkAverage ?? [])
                : []
              ).map((row) => ({
                contractorId: NETWORK_AVERAGE_CONTRACTOR_ID,
                contractorName: NETWORK_AVERAGE_LABEL,
                month: row.month,
                totalCost: row.totalCost,
              })),
            ],
    }),
    // Contractor Count Per Company
    getContractorCountPerCompany: builder.query<
      ContractorMonthlyCountResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly-count/company`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyCountResponse[]
          | ContractorMonthlyCountReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) =>
        Array.isArray(response)
          ? response
          : [
              ...(response.monthlyCountPerContractor ?? []),
              ...(arg.networkAverage
                ? (response.monthlyCountNetworkAverage ?? [])
                : []
              ).map((row) => ({
                contractorId: NETWORK_AVERAGE_CONTRACTOR_ID,
                contractorName: NETWORK_AVERAGE_LABEL,
                month: row.month,
                segmentCount: row.networkAverage,
              })),
            ],
    }),
    // Contractor On Time Performance
    getContractorOnTimePerformance: builder.query<
      ContractorMonthlyOtpResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly/on-time-performance`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyOtpResponse[]
          | ContractorMonthlyOtpReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) =>
        Array.isArray(response)
          ? response
          : [
              ...(response.monthlyOtpPerContractor ?? []),
              ...(arg.networkAverage
                ? (response.monthlyOtpNetworkAverage ?? [])
                : []
              ).map((row) => ({
                contractorId: NETWORK_AVERAGE_CONTRACTOR_ID,
                contractorName: NETWORK_AVERAGE_LABEL,
                month: row.month,
                onTimeCount: 0,
                lateCount: 0,
                totalCount: 0,
                onTimePercentage: row.networkAverage,
                isNetworkAverage: true,
              })),
            ],
    }),
    // Contractor Cost Per Level of Service
    getContractorCostPerLevelOfService: builder.query<
      ContractorMonthlyLosCostResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly-cost/los`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyLosCostResponse[]
          | ContractorMonthlyLosCostReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) =>
        Array.isArray(response)
          ? response
          : [
              ...(response.monthlyCostPerLos ?? []),
              ...(arg.networkAverage
                ? (response.monthlyCostNetworkAveragePerLos ?? [])
                : []
              ).map((row) => ({
                month: row.month,
                losId: -Math.abs(row.losId) - 1,
                losName: row.losName + ' Network Average',
                totalCost: row.networkAverage,
              })),
            ],
    }),
    // Contractor Count Per Level of Service
    getContractorCountPerLevelOfService: builder.query<
      ContractorMonthlyLosCountResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly-count/los`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyLosCountResponse[]
          | ContractorMonthlyLosCountReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) => {
        if (Array.isArray(response)) return response;

        const rows = response.monthlyCountPerLos ?? [];
        const losNames = new Map(
          rows.map((row) => [row.losId, row.levelOfServiceName])
        );

        return [
          ...rows,
          ...(arg.networkAverage
            ? (response.monthlyCountNetworkAveragePerLos ?? [])
            : []
          ).map((row) => ({
            month: row.month,
            losId: -Math.abs(row.losId) - 1,
            levelOfServiceName:
              (losNames.get(row.losId) ?? 'LOS ' + row.losId) +
              ' Network Average',
            segmentCount: row.networkAverage,
          })),
        ];
      },
    }),
    // Contractor Cost Per County
    getContractorCostPerCounty: builder.query<
      ContractorMonthlyCountyCostResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly-cost/county`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyCountyCostResponse[]
          | ContractorMonthlyCountyCostReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) =>
        Array.isArray(response)
          ? response
          : [
              ...(response.monthlyCostPerCounty ?? []),
              ...(arg.networkAverage
                ? (response.monthlyCostNetworkAveragePerCounty ?? [])
                : []
              ).map((row) => ({
                month: row.month,
                county: row.county + ' Network Average',
                totalCost: row.networkAverage,
              })),
            ],
    }),
    // Contractor Count Per County
    getContractorCountPerCounty: builder.query<
      ContractorMonthlyCountyCountResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly-count/county`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response:
          | ContractorMonthlyCountyCountResponse[]
          | ContractorMonthlyCountyCountReportResponse,
        _meta,
        arg: ContractorMonthlyFilterBody
      ) =>
        Array.isArray(response)
          ? response
          : [
              ...(response.monthlyCountPerCounty ?? []),
              ...(arg.networkAverage
                ? (response.monthlyCountNetworkAveragePerCounty ?? [])
                : []
              ).map((row) => ({
                month: row.month,
                countyId: -Math.abs(row.countyId) - 1,
                countyName: row.countyName + ' Network Average',
                segmentCount: row.networkAverage,
              })),
            ],
    }),
    // Contractor Rides by Status
    getContractorRidesByStatus: builder.query<
      ContractorMonthlyRidesByStatusResponse[],
      ContractorMonthlyFilterBody
    >({
      query: (body) => ({
        url: `/v2/search/contractor/report/monthly/rides-by-status`,
        method: 'POST',
        body,
      }),
    }),
    getContractorMonthlyCostPerCompanyAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyCostPerCompanyAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyCountPerCompanyAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyCountPerCompanyAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyOnTimePerformanceAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyOnTimePerformanceAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyCostPerLosAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyCostPerLosAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyCountPerLosAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyCountPerLosAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyCostPerCountyAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyCostPerCountyAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyCountPerCountyAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyCountPerCountyAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
    getContractorMonthlyRidesByStatusAggregate: builder.mutation<
      ContractorSegmentsResponse,
      ContractorMonthlyPagedFilterArgs
    >({
      query: ({ page = 0, size = 100, sortBy, sortDir, body }) => ({
        url: CONTRACTOR_TABLE_URLS.monthlyRidesByStatusAggregate,
        method: 'POST',
        params: {
          page,
          size,
          ...(sortBy && sortDir ? { sortBy, sortDir } : {}),
        },
        body,
      }),
    }),
  }),
});

export const {
  useGetRideCostByCountyQuery,
  useGetRideReportsRideShareQuery,
  useGetMemberTOPLobDataQuery,
  useGetMonthAgeGroupsQuery,
  useGetMonthCreatedDateQuery,
  useGetMonthCompleteRideQuery,
  useGetTopCountyQuery,
  useGetTopRenderingServiceQuery,
  useGetTopUsedTagsQuery,
  useGetTopHighMileageQuery,
  useGetTopNoShowCountQuery,
  useGetMonthAvgRideCostPerMemberQuery,
  useGetMonthActiveMembersQuery,
  useGetDashboardRecentRideQuery,
  useGetInterValOnTimePerformanceQuery,
  useGetRideMonthsPerformanceQuery,
  useGetRideLsVsRoutineQuery,
  useGetInterValOnLSTimePerformanceQuery,
  // useGetRideCompleteBoxQuery,
  useGetStatisticBoxQuery,
  useGetRideDashboardHeatMapQuery,
  // useGetRideDistinctMembersQuery,
  // useGetRideOnTimePerformanceQuery,
  useGetContractorTopCostMembersMutation,
  useGetCostPerRideTrendAggregateMutation,
  useGetContractorRideCompletionRateAggregateMutation,
  useGetContractorRideTypeComparisonAggregateMutation,
  useGetContractorAverageTimeToArrivalAggregateMutation,
  useGetTop5IncompleteDriverNoShowCitiesAggregateMutation,
  useGetContractorRidesByLosAggregateMutation,
  // useGetRideCostQuery,
  useGetRideInCompleteBreakDownQuery,
  useGetRideDashboardAverageMilesQuery,
  useGetRideMonthStatusQuery,
  useGetRideMonthAllStatusQuery,
  useGetRideInCompleteLSQuery,
  useGetRideDashboardMemberQuery,
  // TODO this request repeated
  useGetRideTopLocationQuery,
  useGetRideTopServiceTypesQuery,
  useGetRideSearchLocationQuery,
  useGetActiveMembersQuery,
  useLazyGetMemberDashboardMonthShowCountQuery,
  useGetRideStatusesQuery,
  //Contractor Reports
  useGetContractorTopCostSegmentsMutation,
  useGetContractorTopCostRankingMutation,
  useGetContractorNoShowSegmentsMutation,
  useGetContractorNoShowRankingMutation,
  useGetContractorNoShowAggregatedMutation,
  useGetContractorCityNoShowRankingMutation,
  useGetContractorRideTypeChartMutation,
  useGetContractorLosChartMutation,
  // CostComparisonChart
  useGetContractorCostComparisonChartMutation,
  useGetContractorCostComparisonAggregateMutation,
  useGetContractorReportSegmentsMutation,
  useGetContractorDashboardSegmentsMutation,
  useExportRideTableFileMutation,
  // Contractor Dashboard
  useGetCostPerRideTrendQuery,
  useGetContractorRideCompletionRateQuery,
  useGetContractorCostComparisonQuery,
  useGetContractorAverageTimeToArrivalQuery,
  useGetRideTypeComparisonQuery,
  useGetTop5IncompleteDriverNoShowCitiesQuery,
  useGetRidesByLevelOfServiceQuery,
  useGetContractorCostPerCompanyQuery,
  useGetContractorCountPerCompanyQuery,
  useGetContractorOnTimePerformanceQuery,
  useGetContractorCostPerLevelOfServiceQuery,
  useGetContractorCountPerLevelOfServiceQuery,
  useGetContractorCostPerCountyQuery,
  useGetContractorCountPerCountyQuery,
  useGetContractorRidesByStatusQuery,
  useGetContractorMonthlyCostPerCompanyAggregateMutation,
  useGetContractorMonthlyCountPerCompanyAggregateMutation,
  useGetContractorMonthlyOnTimePerformanceAggregateMutation,
  useGetContractorMonthlyCostPerLosAggregateMutation,
  useGetContractorMonthlyCountPerLosAggregateMutation,
  useGetContractorMonthlyCostPerCountyAggregateMutation,
  useGetContractorMonthlyCountPerCountyAggregateMutation,
  useGetContractorMonthlyRidesByStatusAggregateMutation,
} = dashboardChartsApi;
